import { z } from 'zod';
import { decorationCategorySchema, decorationKindSchema, rosterFlagSchema, starThresholdsSchema, starTierSchema } from './profiles.js';
/**
 * Re-exported so existing `import { rosterFlagSchema } from './contracts.js'`
 * (and `from '@clan-heart/shared'`) call sites keep working — the schema
 * itself lives in profiles.ts now, since `updateProfileInputSchema` there
 * needs it and profiles.ts never imports from contracts.ts.
 */
export { rosterFlagSchema };
/**
 * The API contract both sides build against. The API validates request bodies
 * with the `*InputSchema`s and returns objects shaped like the response types;
 * the web app imports the same types. Change it here and both sides break at
 * compile time rather than drifting.
 *
 * Timestamps are ISO-8601 strings, matching the TEXT columns in the database.
 */
// ---------------------------------------------------------------------------
// roles
// ---------------------------------------------------------------------------
/**
 * `owner` is whoever's Discord id is in OWNER_DISCORD_ID on the server — it is
 * never granted or removed through the API. Roles below owner are assigned on
 * the site (not read from Discord): the owner promotes members to
 * `first_officer` or `officer`; a `first_officer` can in turn promote/demote
 * between `officer` and `member`. `officer` and `first_officer` both help run
 * accomplishments and review claims — see `canManage`.
 */
export const roleSchema = z.enum(['owner', 'first_officer', 'officer', 'member']);
/** Roles allowed to create accomplishments and review claims. */
export function canManage(role) {
    return role === 'owner' || role === 'first_officer' || role === 'officer';
}
/**
 * Whether `actor` may set `targetCurrent`'s role to `next`. Pure and shared
 * with the web app so the UI only ever offers choices the API will accept.
 *
 * - `owner` is never an assignable `next` — it comes only from
 *   `OWNER_DISCORD_ID` — and the owner's own role can never be changed here.
 * - The owner may set anyone else to `first_officer`, `officer` or `member`.
 * - A `first_officer` may move someone between `officer` and `member`, but
 *   never touches the owner or another `first_officer`.
 * - `officer` and `member` assign nothing.
 */
export function canAssignRole(actor, targetCurrent, next) {
    if (next === 'owner')
        return false;
    if (actor === 'owner')
        return targetCurrent !== 'owner';
    if (actor === 'first_officer') {
        return ((targetCurrent === 'officer' || targetCurrent === 'member') && (next === 'officer' || next === 'member'));
    }
    return false;
}
/**
 * Whether `actor` may remove a roster entry / member. `targetRole` is `null`
 * for a roster entry with no linked account — any manager may remove those.
 * Otherwise: the owner removes anyone but the owner; a `first_officer`
 * removes officers and members; an `officer` removes members only.
 */
export function canRemove(actor, targetRole) {
    if (targetRole === null)
        return canManage(actor);
    if (actor === 'owner')
        return targetRole !== 'owner';
    if (actor === 'first_officer')
        return targetRole === 'officer' || targetRole === 'member';
    if (actor === 'officer')
        return targetRole === 'member';
    return false;
}
// ---------------------------------------------------------------------------
// users / members
// ---------------------------------------------------------------------------
/**
 * Recruit-approval state. Only `approved` grants cave access; `removed` and
 * `rejected` block sign-in outright (see `statusForSignIn` in the API).
 */
export const userStatusSchema = z.enum(['pending', 'approved', 'rejected', 'removed']);
/** GET /api/me */
export const userSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
    role: roleSchema,
    status: userStatusSchema,
    /** The name the crew knows them by — their linked roster entry's name, else displayName. See `crewName` in api/src/profiles/service.ts. */
    crewName: z.string(),
});
/** One row of GET /api/members — the roster and leaderboard in one. */
export const memberSummarySchema = userSchema.extend({
    joinedAt: z.string().datetime(),
    approvedCount: z.number().int(),
    points: z.number().int(),
    /** Broadsides visitors have fired from this member's ship on their profile (a hidden gimmick). */
    broadsides: z.number().int(),
    /** Their public profile is at /crew/<profileSlug>; null until one is assigned. */
    profileSlug: z.string().nullable(),
});
/** PATCH /api/members/:id/role. `owner` cannot be granted; see `canAssignRole` for who may call this on whom. */
export const setRoleInputSchema = z.object({
    role: z.enum(['first_officer', 'officer', 'member']),
});
// ---------------------------------------------------------------------------
// accomplishments
// ---------------------------------------------------------------------------
export const claimStatusSchema = z.enum(['pending', 'approved', 'rejected']);
export const accomplishmentSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
    points: z.number().int(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    archivedAt: z.string().datetime().nullable(),
    /** How it is drawn on a profile — see DECORATION_CATEGORIES/decorationKindSchema in profiles.ts. */
    decoration: decorationKindSchema,
    category: decorationCategorySchema,
    /** Whether any number of approved claims count (vs. at most one live claim). */
    repeatable: z.boolean(),
    /** Service-star thresholds ([bronze, silver, gold] approved-claim counts), or [] if not configured — always [] when `repeatable` is false. */
    starThresholds: starThresholdsSchema,
    /** An uploaded image path `/api/flags/<uuid>`, or null for the hatched placeholder. */
    art: z.string().nullable(),
    /**
     * The viewer's PENDING claim on this accomplishment if they have one,
     * otherwise their most recent claim, or null if they have never claimed
     * it. A per-request projection, not a stored column.
     */
    myClaim: z
        .object({
        id: z.string(),
        status: claimStatusSchema,
        reviewNote: z.string().nullable(),
    })
        .nullable(),
    /** How many of the viewer's claims on this accomplishment are approved. */
    myApprovedCount: z.number().int(),
    /** The viewer's star tier from `myApprovedCount` against `starThresholds` — see `starTier`. */
    myStars: z.object({ tier: starTierSchema, next: z.number().int().nullable() }),
    /**
     * Who has earned it: one entry per member who has at least one approved
     * claim, oldest-first-approval first, each carrying their approved-claim
     * `count`. Sent to every signed-in crew member.
     */
    holders: z
        .array(z.object({
        id: z.string(),
        displayName: z.string(),
        avatarUrl: z.string().nullable(),
        /** This member's first approval. */
        approvedAt: z.string().datetime().nullable(),
        /** How many of this member's claims are approved. */
        count: z.number().int(),
    }))
        .optional(),
});
const title = z.string().trim().min(1).max(120);
const description = z.string().trim().max(2000);
const points = z.number().int().min(0).max(10_000);
/** POST /api/accomplishments — owner/officer only. */
export const createAccomplishmentInputSchema = z.object({
    title,
    description: description.default(''),
    points: points.default(0),
    decoration: decorationKindSchema.default('ribbon'),
    category: decorationCategorySchema.default('service'),
    repeatable: z.boolean().default(false),
    starThresholds: starThresholdsSchema.default([]),
    art: z.string().nullable().default(null),
});
/**
 * PATCH /api/accomplishments/:id — owner/officer only. `archived` hides or
 * restores it. Setting `repeatable` from true to false while any
 * non-rejected claim exists on it is refused (409 `{error:'repeatable
 * locked'}`) by the route, not by this schema.
 */
export const updateAccomplishmentInputSchema = z
    .object({
    title,
    description,
    points,
    archived: z.boolean(),
    decoration: decorationKindSchema,
    category: decorationCategorySchema,
    repeatable: z.boolean(),
    starThresholds: starThresholdsSchema,
    art: z.string().nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update.' });
// ---------------------------------------------------------------------------
// claims
// ---------------------------------------------------------------------------
/**
 * Only http(s) links. Evidence is rendered as a clickable link, so a
 * `javascript:` URL here would be a stored XSS waiting for an officer to click.
 */
export const evidenceUrlSchema = z
    .string()
    .trim()
    .url()
    .max(2000)
    .refine((u) => /^https?:\/\//i.test(u), { message: 'Evidence must be an http(s) link.' });
/** POST /api/accomplishments/:id/claims — any signed-in member. */
export const submitClaimInputSchema = z.object({
    note: z.string().trim().max(1000).optional(),
    evidenceUrl: evidenceUrlSchema.optional(),
});
/** POST /api/claims/:id/review — owner/officer only. */
export const reviewClaimInputSchema = z.object({
    decision: z.enum(['approve', 'reject']),
    note: z.string().trim().max(1000).optional(),
});
/**
 * POST /api/accomplishments/:id/award — a manager awards an accomplishment to
 * a crew member directly, without a claim from them. Recorded as an approved
 * claim (the awarder as reviewer, the note as review note).
 */
export const awardInputSchema = z.object({
    userId: z.string().min(1),
    note: z.string().trim().max(1000).optional(),
});
export const claimSchema = z.object({
    id: z.string(),
    status: claimStatusSchema,
    note: z.string().nullable(),
    evidenceUrl: z.string().nullable(),
    submittedAt: z.string().datetime(),
    reviewedAt: z.string().datetime().nullable(),
    reviewNote: z.string().nullable(),
    accomplishment: z.object({ id: z.string(), title: z.string(), points: z.number().int() }),
    member: z.object({ id: z.string(), displayName: z.string(), avatarUrl: z.string().nullable() }),
    reviewer: z.object({ id: z.string(), displayName: z.string() }).nullable(),
});
// ---------------------------------------------------------------------------
// roster
// ---------------------------------------------------------------------------
/**
 * The one roster that drives both the public About page's Hall of Members
 * and cave access. Display order within a section is `position` ascending.
 */
export const rosterSectionSchema = z.enum(['officers', 'members', 'honorable', 'dockmates', 'feared']);
const rosterName = z.string().trim().min(1).max(60);
const rosterSubtitle = z.string().trim().max(80);
/**
 * GET /api/roster — public. Never carries `discordUserId` or any other
 * linkage info; see `rosterManageEntrySchema` for the manager's view.
 */
export const rosterEntrySchema = z.object({
    id: z.string(),
    name: rosterName,
    subtitle: rosterSubtitle,
    section: rosterSectionSchema,
    flag: rosterFlagSchema,
    position: z.number().int(),
    /** The linked account's profile slug (`/crew/<slug>`), or null when unlinked or linked to a non-approved account. */
    profileSlug: z.string().nullable(),
});
/** GET /api/roster/manage — owner/officer-family only; includes the Discord link, if any. */
export const rosterManageEntrySchema = rosterEntrySchema.extend({
    discordUserId: z.string().nullable(),
    /**
     * The linked account's role, for showing only the controls the viewer may
     * use (`canRemove`). Null when unlinked or linked to someone who never
     * signed in; 'owner' for OWNER_DISCORD_ID even before their first sign-in.
     */
    linkedRole: roleSchema.nullable(),
});
/** POST /api/roster */
export const createRosterEntryInputSchema = z.object({
    name: rosterName,
    subtitle: rosterSubtitle.default(''),
    section: rosterSectionSchema,
    flag: rosterFlagSchema,
    discordUserId: z.string().optional(),
});
/** PATCH /api/roster/:id */
export const updateRosterEntryInputSchema = z
    .object({
    name: rosterName,
    subtitle: rosterSubtitle,
    section: rosterSectionSchema,
    flag: rosterFlagSchema,
    position: z.number().int().min(0),
    discordUserId: z.string().nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update.' });
/** POST /api/roster/reorder */
export const reorderRosterInputSchema = z.object({
    section: rosterSectionSchema,
    ids: z.array(z.string()).min(1),
});
// ---------------------------------------------------------------------------
// "one crew member, one place" — the member's own profile, and the
// officer/owner "manage" view of someone else's
// ---------------------------------------------------------------------------
/**
 * GET/PATCH /api/me/profile — the signed-in member's own card: their crew
 * name, rank (roster subtitle, else their role label — read-only here, set
 * by an officer), section, role, flag, motto, favourite ship and flagship
 * name. `favouriteShip`/`flagshipName` are plain ids/strings here (unlike
 * `crewProfileSchema`'s richer `favouriteShip` object) — this is the editable
 * form, not the public display.
 */
export const myProfileSchema = z.object({
    slug: z.string(),
    name: z.string(),
    rank: z.string(),
    section: rosterSectionSchema,
    role: roleSchema,
    flag: rosterFlagSchema,
    motto: z.string(),
    favouriteShip: z.string().nullable(),
    flagshipName: z.string().nullable(),
});
/**
 * GET /api/crew/:slug/manage — owner/officer-family only. The "Officer
 * controls" panel on someone's profile: their current rank/section/role/
 * position/flag/motto, plus what the viewer specifically may do to them —
 * see `crewAdminPermissions`.
 */
export const crewMemberAdminSchema = z.object({
    userId: z.string(),
    rosterEntryId: z.string(),
    name: z.string(),
    rank: z.string(),
    section: rosterSectionSchema,
    role: roleSchema,
    position: z.number().int(),
    flag: rosterFlagSchema,
    motto: z.string(),
    favouriteShip: z.string().nullable(),
    flagshipName: z.string().nullable(),
    /** Roles the viewer may set this person to — see `crewAdminPermissions`. */
    canAssignRoles: z.array(roleSchema),
    canRemove: z.boolean(),
});
/**
 * Pure: what the viewer may do to the target on the "Officer controls"
 * panel — which roles they may assign (never including the target's current
 * role, and only ones `canAssignRole` allows), and whether they may remove
 * this person (never themselves, and `canRemove` already refuses the owner
 * as a target). Shared with the web app so the UI only ever offers controls
 * the API will accept.
 */
export function crewAdminPermissions(viewerRole, viewerId, targetRole, targetId) {
    const canAssignRoles = roleSchema.options.filter((next) => next !== targetRole && canAssignRole(viewerRole, targetRole, next));
    return {
        canAssignRoles,
        canRemove: viewerId !== targetId && canRemove(viewerRole, targetRole),
    };
}
/** GET /api/recruits — pending users awaiting an owner/officer decision. */
export const recruitSchema = z.object({
    id: z.string(),
    discordUserId: z.string(),
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
    createdAt: z.string().datetime(),
});
/**
 * GET /api/recruits/turned-away — everyone who was rejected as a recruit or
 * removed from the crew, newest decision first, so a mistake can be undone
 * with POST /api/recruits/:userId/readmit.
 */
export const turnedAwaySchema = z.object({
    id: z.string(),
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
    status: z.enum(['rejected', 'removed']),
    decidedBy: z.string().nullable(),
    decidedAt: z.string().datetime().nullable(),
});
//# sourceMappingURL=contracts.js.map